/**
 * 3-panel UI for creating CV-JD analyses.
 *
 *  ┌──────────┬─────────────────────┬──────────┐
 *  │ [search] │  ┌─ JDs (max 5) ─┐  │ [search] │
 *  │ JD list  │  │ chip chip …   │  │ CV list  │
 *  │ (drag)   │  └───────────────┘  │ (drag)   │
 *  │          │  ┌─ CVs (max 10) ┐  │          │
 *  │          │  │ chip chip …   │  │          │
 *  │          │  └───────────────┘  │          │
 *  │          │  [Analyse N×M btn]  │          │
 *  └──────────┴─────────────────────┴──────────┘
 *
 * Selection: click to toggle, or drag item → drop zone.
 * Limits: max 5 JDs, max 10 CVs.
 */

import { useState, useCallback } from 'react';
import { Search, Loader2, CheckCircle2, FileText, User, X, GripVertical, Plus, RotateCcw, AlertCircle } from 'lucide-react';
import { toast, useAuth } from '@aaas/shared';
import { useTranslation } from 'react-i18next';
import { useJobMappingsQuery } from '../../hooks/useAnalysisQueries';
import { AnalysisDetailPanel } from './AnalysisDetailPanel';
import type { AnalysisPanelState } from './AnalysisDetailPanel';
import type { JobDescription, JobCVCard, AnalysisTriggerResponse, AnalysisStatus, AnalysisMetadata } from '../../types';

const MAX_JDS = 5;
const MAX_CVS = 10;

// ── Drag data helpers ─────────────────────────────────────────────────────────

const DRAG_TYPE_JD = 'application/x-ats-jd';
const DRAG_TYPE_CV = 'application/x-ats-cv';

// ── API error helper ──────────────────────────────────────────────────────────

function getApiError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
    if (detail) return detail;
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('ats');
  const cfg: Record<string, { label: string; className: string }> = {
    processed:  { label: t('matching.status.ready'),      className: 'bg-green-100 text-green-700' },
    processing: { label: t('matching.status.processing'), className: 'bg-blue-100 text-blue-600' },
    pending:    { label: t('matching.status.pending'),    className: 'bg-gray-100 text-gray-500' },
    error:      { label: t('matching.status.error'),      className: 'bg-red-100 text-red-600' },
    failed:     { label: t('matching.status.failed'),     className: 'bg-red-100 text-red-600' },
  };
  const c = cfg[status] ?? { label: status, className: 'bg-gray-100 text-gray-500' };
  return (
    <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${c.className}`}>
      {c.label}
    </span>
  );
}

// ── Selected item row inside drop zone ────────────────────────────────────────

function SelectedItem({ icon, label, onRemove }: { icon: React.ReactNode; label: string; onRemove: () => void }) {
  const { t } = useTranslation('ats');
  return (
    <div className="group flex items-center gap-2.5 px-3 py-2.5 bg-white rounded-lg border border-orange-200 hover:border-orange-400 hover:shadow-sm transition-all">
      <span className="flex-shrink-0 text-orange-400">{icon}</span>
      <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 truncate" title={label}>
        {label}
      </span>
      <button
        onClick={onRemove}
        aria-label={t('matchingCreate.removeSelectedAria', { name: label })}
        className="flex-shrink-0 p-1 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Drop zone ─────────────────────────────────────────────────────────────────

interface DropZoneProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  max: number;
  chips: { id: string; name: string }[];
  acceptType: string;
  onDrop: (id: string) => void;
  onRemove: (id: string) => void;
  placeholder: string;
}

function DropZone({ icon, label, count, max, chips, acceptType, onDrop, onRemove, placeholder }: DropZoneProps) {
  const { t } = useTranslation('ats');
  const [isDragOver, setIsDragOver] = useState(false);
  const full = count >= max;

  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(acceptType)) {
      e.preventDefault();
      if (!full) setIsDragOver(true);
    }
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const id = e.dataTransfer.getData(acceptType);
    if (id) onDrop(id);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`flex flex-col rounded-xl border-2 border-dashed transition-colors min-h-[260px] ${
        isDragOver && !full
          ? 'border-orange-400 bg-orange-50'
          : full
          ? 'border-orange-200 bg-orange-50/40'
          : 'border-gray-200 hover:border-orange-300 bg-white'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <span className={full ? 'text-orange-400' : 'text-gray-400'}>{icon}</span>
          {label}
        </div>
        <span className={`text-xs font-semibold tabular-nums px-1.5 py-0.5 rounded-full ${
          full ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'
        }`}>
          {count}/{max}
        </span>
      </div>

      {/* Items */}
      <div className="flex-1 px-3 pb-3 space-y-1.5 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
        {chips.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full min-h-[160px] gap-1.5">
            <p className="text-xs text-gray-400 text-center">{placeholder}</p>
          </div>
        ) : (
          chips.map((c) => (
            <SelectedItem
              key={c.id}
              icon={icon}
              label={c.name}
              onRemove={() => onRemove(c.id)}
            />
          ))
        )}
      </div>

      {full && (
        <p className="text-xs text-orange-500 text-center pb-2.5">{t('matching.maxReached', { max })}</p>
      )}
    </div>
  );
}

// ── Draggable list row ────────────────────────────────────────────────────────

function ListRow({
  id,
  label,
  status,
  selected,
  disabled,
  dragType,
  onClick,
}: {
  id: string;
  label: string;
  status: string;
  selected: boolean;
  disabled: boolean;
  dragType: string;
  onClick: () => void;
}) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(dragType, id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div
      draggable={!selected && !disabled}
      onDragStart={handleDragStart}
      onClick={disabled ? undefined : onClick}
      className={`group flex items-center gap-2 px-2 py-2 rounded-md border transition-colors select-none ${
        selected
          ? 'bg-orange-50 border-orange-300 cursor-default'
          : disabled
          ? 'border-transparent opacity-40 cursor-not-allowed'
          : 'border-transparent hover:bg-gray-50 hover:border-gray-200 cursor-pointer'
      }`}
    >
      {/* Drag handle — only shown when draggable */}
      {!selected && !disabled && (
        <GripVertical className="h-3.5 w-3.5 text-gray-300 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
      {selected && <CheckCircle2 className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />}
      {disabled && !selected && <span className="h-3.5 w-3.5 flex-shrink-0" />}

      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-gray-800 truncate">{label}</span>
      </span>
      <StatusBadge status={status} />
    </div>
  );
}

// ── Preview: status + score badges ────────────────────────────────────────────

function PreviewStatusBadge({ status }: { status: AnalysisStatus }) {
  const { t } = useTranslation('ats');
  const cfg = {
    analyzing: { label: t('matching.status.analyzing'), className: 'bg-blue-50 text-blue-600', icon: <Loader2 className="h-3 w-3 animate-spin" /> },
    completed:  { label: t('matching.status.done'),      className: 'bg-green-50 text-green-700', icon: null },
    pending:    { label: t('matching.status.pending'),   className: 'bg-gray-100 text-gray-500',  icon: null },
    error:      { label: t('matching.status.error'),     className: 'bg-red-50 text-red-600',     icon: <AlertCircle className="h-3 w-3" /> },
  } as const;
  const c = cfg[status] ?? cfg.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${c.className}`}>
      {c.icon}{c.label}
    </span>
  );
}

function PreviewScoreBadge({ score }: { score: number | null }) {
  if (score === null) return null;
  const { bg, text, bar } =
    score >= 80 ? { bg: 'bg-green-50',  text: 'text-green-700',  bar: 'bg-green-400' } :
    score >= 60 ? { bg: 'bg-yellow-50', text: 'text-yellow-700', bar: 'bg-yellow-400' } :
                  { bg: 'bg-red-50',    text: 'text-red-700',    bar: 'bg-red-400' };
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg ${bg}`}>
      <span className={`text-xs font-bold tabular-nums ${text}`}>{score}%</span>
      <div className="w-8 h-1 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

// ── Preview: Kanban column per JD ─────────────────────────────────────────────

interface PreviewColumnProps {
  jd: JobDescription;
  cvCards: JobCVCard[];
  mappingsByCv: Map<string, { status: AnalysisStatus; score: number | null; metadata: AnalysisMetadata | null }>;
  onCardClick: (state: AnalysisPanelState) => void;
}

function PreviewColumn({ jd, cvCards, mappingsByCv, onCardClick }: PreviewColumnProps) {
  return (
    <div className="flex-shrink-0 w-56 bg-gray-100 rounded-lg flex flex-col self-stretch">
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-start gap-2">
          <FileText className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate leading-snug" title={jd.name}>
              {jd.name}
            </h3>
            <span className="inline-flex items-center justify-center mt-1 w-5 h-5 rounded-full bg-gray-200 text-xs font-medium text-gray-700">
              {cvCards.length}
            </span>
          </div>
        </div>
      </div>
      <div className="flex-1 p-2 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {cvCards.map((cv) => {
          const mapping = mappingsByCv.get(cv.id);
          const status: AnalysisStatus = mapping?.status ?? 'pending';
          return (
            <div
              key={cv.id}
              onClick={() => onCardClick({
                cvName: cv.name,
                jdName: jd.name,
                cvId: cv.id,
                jdId: jd.id,
                status,
                score: mapping?.score ?? null,
                metadata: mapping?.metadata ?? null,
              })}
              className="bg-white rounded-lg border border-gray-200 p-2.5 shadow-sm hover:border-orange-300 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex items-start gap-2">
                <User className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate" title={cv.name}>{cv.name}</p>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    <PreviewStatusBadge status={status} />
                    {status === 'completed' && <PreviewScoreBadge score={mapping?.score ?? null} />}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  jobId: string;
  jds: JobDescription[];
  cvCards: JobCVCard[];
  isJDsLoading: boolean;
  isCVsLoading: boolean;
  onTrigger: (cvId: string, jdId: string) => Promise<AnalysisTriggerResponse>;
}

// ── Component ────────────────────────────────────────────────────────────────

interface PreviewState {
  jds: JobDescription[];
  cvCards: JobCVCard[];
}

export function MatchingCreateView({ jobId, jds, cvCards, isJDsLoading, isCVsLoading, onTrigger }: Props) {
  const { t } = useTranslation('ats');
  const [selectedJDIds, setSelectedJDIds] = useState<Set<string>>(new Set());
  const [selectedCVIds, setSelectedCVIds] = useState<Set<string>>(new Set());
  const [jdSearch, setJDSearch] = useState('');
  const [cvSearch, setCVSearch] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [selectedPanel, setSelectedPanel] = useState<AnalysisPanelState | null>(null);

  const { currentWorkspace, idToken } = useAuth();

  // Poll mappings only when in preview mode
  const { data: mappingsData } = useJobMappingsQuery({
    jobId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!preview,
  });

  // ── Selection helpers ───────────────────────────────────────────────────────

  const toggleJD = useCallback((id: string) => {
    setSelectedJDIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= MAX_JDS) {
          toast.warning(t('matching.maxJds', { max: MAX_JDS }));
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleCV = useCallback((id: string) => {
    setSelectedCVIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= MAX_CVS) {
          toast.warning(t('matching.maxCvs', { max: MAX_CVS }));
          return prev;
        }
        next.add(id);
      }
      return next;
    });
  }, []);

  const addJDFromDrop = useCallback((id: string) => {
    const jd = jds.find((j) => j.id === id);
    if (!jd || jd.processing_status !== 'processed') {
      toast.error(t('matching.jdNotReady'));
      return;
    }
    setSelectedJDIds((prev) => {
      if (prev.has(id)) return prev;
      if (prev.size >= MAX_JDS) {
        toast.warning(t('matching.maxJds', { max: MAX_JDS }));
        return prev;
      }
      return new Set([...prev, id]);
    });
  }, [jds]);

  const addCVFromDrop = useCallback((id: string) => {
    const cv = cvCards.find((c) => c.id === id);
    if (!cv || cv.processing_status !== 'processed') {
      toast.error(t('matching.cvNotReady'));
      return;
    }
    setSelectedCVIds((prev) => {
      if (prev.has(id)) return prev;
      if (prev.size >= MAX_CVS) {
        toast.warning(t('matching.maxCvs', { max: MAX_CVS }));
        return prev;
      }
      return new Set([...prev, id]);
    });
  }, [cvCards]);

  // ── Analyse ─────────────────────────────────────────────────────────────────

  const totalPairs = selectedJDIds.size * selectedCVIds.size;
  const canAnalyze = selectedJDIds.size > 0 && selectedCVIds.size > 0 && !isAnalyzing;

  const handleAnalyze = async () => {
    if (!canAnalyze) return;
    setIsAnalyzing(true);

    const pairs: [string, string][] = [];
    for (const cvId of selectedCVIds) {
      for (const jdId of selectedJDIds) {
        pairs.push([cvId, jdId]);
      }
    }

    let successCount = 0;
    let skipCount = 0;

    await Promise.allSettled(
      pairs.map(async ([cvId, jdId]) => {
        try {
          const result = await onTrigger(cvId, jdId);
          // 'pending'/'analyzing' = freshly queued; 'completed'/'error' = already ran
          if (result.analysis_status === 'pending' || result.analysis_status === 'analyzing') {
            successCount++;
          } else {
            skipCount++; // completed or error — already ran
          }
        } catch (err) {
          toast.error(getApiError(err));
        }
      }),
    );

    setIsAnalyzing(false);

    if (successCount > 0) {
      toast.success(t('matching.submitted', { count: successCount }));
    }
    if (skipCount > 0) {
      toast.info(t('matching.pairsSkipped', { count: skipCount }));
    }

    if (successCount > 0 || skipCount > 0) {
      // Enter preview mode with the triggered JDs and CVs
      const triggeredJDs = jds.filter((j) => selectedJDIds.has(j.id));
      const triggeredCVs = cvCards.filter((c) => selectedCVIds.has(c.id));
      setPreview({ jds: triggeredJDs, cvCards: triggeredCVs });
      setSelectedJDIds(new Set());
      setSelectedCVIds(new Set());
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────────

  const filteredJDs = jds.filter((j) => j.name.toLowerCase().includes(jdSearch.toLowerCase()));
  const filteredCVs = cvCards.filter((c) => c.name.toLowerCase().includes(cvSearch.toLowerCase()));

  const selectedJDChips = jds
    .filter((j) => selectedJDIds.has(j.id))
    .map((j) => ({ id: j.id, name: j.name }));

  const selectedCVChips = cvCards
    .filter((c) => selectedCVIds.has(c.id))
    .map((c) => ({ id: c.id, name: c.name }));

  const jdsFull = selectedJDIds.size >= MAX_JDS;
  const cvsFull = selectedCVIds.size >= MAX_CVS;

  // ── Derive preview data ───────────────────────────────────────────────────────

  let previewMiddle: React.ReactNode = null;
  if (preview) {
    const allMappings = mappingsData?.items ?? [];
    const previewCvIds = new Set(preview.cvCards.map((c) => c.id));
    const mappingsByJd = new Map<string, Map<string, { status: AnalysisStatus; score: number | null; metadata: AnalysisMetadata | null }>>();
    for (const jd of preview.jds) mappingsByJd.set(jd.id, new Map());
    for (const m of allMappings) {
      if (mappingsByJd.has(m.jd_id) && previewCvIds.has(m.cv_id)) {
        mappingsByJd.get(m.jd_id)!.set(m.cv_id, {
          status: m.analysis_status,
          score: m.analysis_score,
          metadata: m.analysis_metadata,
        });
      }
    }
    previewMiddle = (
      <div className="flex flex-col h-4/5 gap-2 px-12 pt-2 pb-3">
        <div className="flex items-center justify-center gap-2 flex-shrink-0">
          <span className="text-xs font-medium text-gray-500">
            {preview.jds.length} JD × {preview.cvCards.length} CV
          </span>
          <button
            onClick={() => setPreview(null)}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded-md hover:bg-gray-50 transition-colors text-gray-500"
          >
            <RotateCcw className="h-3 w-3" />
            {t('matching.new')}
          </button>
        </div>
        <div className="flex-1 min-h-0 flex items-stretch justify-center gap-3 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {preview.jds.map((jd) => (
            <PreviewColumn
              key={jd.id}
              jd={jd}
              cvCards={preview.cvCards}
              mappingsByCv={mappingsByJd.get(jd.id)!}
              onCardClick={setSelectedPanel}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="flex h-full overflow-hidden">

      {/* ── Left: JD list ────────────────────────────────────────────────────── */}
      <div className="w-64 flex-shrink-0 flex flex-col border-r border-gray-200">
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder={t('matching.searchJd')}
              value={jdSearch}
              onChange={(e) => setJDSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>
          <p className="mt-1.5 text-xs text-gray-400">
            {t('matching.jdHint', { max: MAX_JDS })}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {isJDsLoading ? (
            <div className="flex justify-center pt-8">
              <Loader2 className="h-5 w-5 text-gray-300 animate-spin" />
            </div>
          ) : filteredJDs.length === 0 ? (
            <p className="text-xs text-gray-400 text-center pt-8">{t('matching.noJds')}</p>
          ) : (
            filteredJDs.map((jd) => (
              <ListRow
                key={jd.id}
                id={jd.id}
                label={jd.name}
                status={jd.processing_status}
                selected={selectedJDIds.has(jd.id)}
                disabled={jd.processing_status !== 'processed' || (jdsFull && !selectedJDIds.has(jd.id))}
                dragType={DRAG_TYPE_JD}
                onClick={() => toggleJD(jd.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Middle: preview result OR drop zones + action ─────────────────────── */}
      <div className="flex-1 min-h-0 flex flex-col justify-center bg-gray-50/40 overflow-hidden">
        {previewMiddle ?? (
          <div className="flex flex-col justify-center gap-3 px-6 py-4 h-full overflow-y-auto">
            <div className="flex gap-3">
              <div className="flex-1">
                <DropZone
                  icon={<FileText className="h-3.5 w-3.5" />}
                  label={t('matching.selectedJds')}
                  count={selectedJDIds.size}
                  max={MAX_JDS}
                  chips={selectedJDChips}
                  acceptType={DRAG_TYPE_JD}
                  onDrop={addJDFromDrop}
                  onRemove={(id) => setSelectedJDIds((prev) => { const n = new Set(prev); n.delete(id); return n; })}
                  placeholder={t('matching.dragJd')}
                />
              </div>
              <div className="flex items-center self-center flex-shrink-0 text-gray-400">
                <Plus className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <DropZone
                  icon={<User className="h-3.5 w-3.5" />}
                  label={t('matching.selectedCvs')}
                  count={selectedCVIds.size}
                  max={MAX_CVS}
                  chips={selectedCVChips}
                  acceptType={DRAG_TYPE_CV}
                  onDrop={addCVFromDrop}
                  onRemove={(id) => setSelectedCVIds((prev) => { const n = new Set(prev); n.delete(id); return n; })}
                  placeholder={t('matching.dragCv')}
                />
              </div>
            </div>
            <div className="flex justify-center">
              <button
                onClick={handleAnalyze}
                disabled={!canAnalyze}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('matching.submitting')}
                  </>
                ) : canAnalyze ? (
                  t('matching.analyzeButton', { count: totalPairs, jds: selectedJDIds.size, cvs: selectedCVIds.size })
                ) : (
                  t('matching.selectPrompt')
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Right: CV list ────────────────────────────────────────────────────── */}
      <div className="w-64 flex-shrink-0 flex flex-col border-l border-gray-200">
        <div className="p-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder={t('matching.searchCv')}
              value={cvSearch}
              onChange={(e) => setCVSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>
          <p className="mt-1.5 text-xs text-gray-400">
            {t('matching.cvHint', { max: MAX_CVS })}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {isCVsLoading ? (
            <div className="flex justify-center pt-8">
              <Loader2 className="h-5 w-5 text-gray-300 animate-spin" />
            </div>
          ) : filteredCVs.length === 0 ? (
            <p className="text-xs text-gray-400 text-center pt-8">{t('matching.noCvs')}</p>
          ) : (
            filteredCVs.map((cv) => (
              <ListRow
                key={cv.id}
                id={cv.id}
                label={cv.name}
                status={cv.processing_status}
                selected={selectedCVIds.has(cv.id)}
                disabled={cv.processing_status !== 'processed' || (cvsFull && !selectedCVIds.has(cv.id))}
                dragType={DRAG_TYPE_CV}
                onClick={() => toggleCV(cv.id)}
              />
            ))
          )}
        </div>
      </div>
    </div>

    {selectedPanel && (
      <AnalysisDetailPanel
        {...selectedPanel}
        onClose={() => setSelectedPanel(null)}
      />
    )}
  </>
  );
}
